/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the ruler.

   Adaptive horizontal ruler above the paper, ported from the classic
   editor's inline ruler (app.tsx ~3864-4180). Word's two indent markers
   live here, where Word puts them — and, since 2026-08-22, ALSO on the
   selected level's own sample paragraph (IndentHandles.tsx), because
   ruler-only meant nobody found them. Both grab points share ONE write
   path (indent-edit.ts): same units, same snapping, same clamps, same
   fine-drag-after-1.5s behaviour, same undoable forma edit
   (contract §2.7) on `levels[i].indent` / `levels[i].hangingIndent`.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { inToPx, resolvePageGeometry } from '../../shared/body-layout';
import type { FormaEdit } from './panels/FormaPalettes';
import { measuredScale } from './spacing-geometry';
import {
  PX_PER_IN,
  applyIndentEdit,
  indentSnapTargets,
  nextIndentValue,
  nudgeIndent,
  nudgeStep,
  pxToIn,
  type IndentKind,
} from './indent-edit';

const RULER_H = 26;

export interface RulerProps {
  forma: StandardConfig;
  /** Which level's indent/hanging markers show — null hides them. */
  levelFocus: number | null;
  edit: FormaEdit;
}

interface DragState {
  type: IndentKind;
  sx: number;
  sv: number;
  fine: boolean;
}

export function Ruler({ forma, levelFocus, edit }: RulerProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const scaleRef = React.useRef(1);
  const geo = resolvePageGeometry(forma);
  const pW = inToPx(geo.pageWidthIn);
  const mLpx = inToPx(geo.marginLeftIn);
  const idx = levelFocus != null ? Math.max(0, Math.min(8, levelFocus - 1)) : null;
  const level = idx != null ? forma.levels?.[idx] : null;

  const [drag, setDrag] = React.useState<DragState | null>(null);
  const [liveVal, setLiveVal] = React.useState<number | null>(null);
  const fineTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const snaps = React.useMemo(() => indentSnapTargets(forma.levels, idx), [forma.levels, idx]);

  const beginDrag = (type: IndentKind) => (e: React.MouseEvent) => {
    if (!level || idx == null) return;
    e.preventDefault();
    e.stopPropagation();
    // The ruler rides inside the canvas's zoom transform, so a client-pixel
    // delta is NOT an inch on the page. Divide the zoom back out (measured
    // off the ruler itself) — a drag then feels identical at every zoom.
    const el = rootRef.current;
    scaleRef.current = el ? measuredScale(el.getBoundingClientRect().width, el.offsetWidth) : 1;
    const sv = type === 'indent' ? level.indent || 0 : level.hangingIndent || 0;
    if (fineTimer.current) clearTimeout(fineTimer.current);
    fineTimer.current = setTimeout(() => setDrag((d) => (d ? { ...d, fine: true } : d)), 1500);
    setDrag({ type, sx: e.clientX, sv, fine: false });
  };

  React.useEffect(() => {
    if (!drag || idx == null || !level) return;
    const baseIndent = level.indent || 0;
    // The math (indent snapping, hanging snapping on the WRAP EDGE, and the
    // Order-3 `hanging >= 0` clamp that stopped the number and the text
    // flying apart) now lives in indent-edit.ts — ONE write path shared with
    // the in-canvas handles, so the two grab points can never disagree.
    const onMove = (ev: MouseEvent) => {
      const dxIn = pxToIn((ev.clientX - drag.sx) / (scaleRef.current || 1));
      const final = nextIndentValue({
        kind: drag.type,
        startValue: drag.sv,
        deltaIn: dxIn,
        baseIndent,
        fine: drag.fine,
        snaps,
      });
      setLiveVal(final);
      applyIndentEdit(edit, idx, drag.type, final);
    };
    const onUp = () => {
      setDrag(null);
      setLiveVal(null);
      if (fineTimer.current) clearTimeout(fineTimer.current);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, idx, level, edit, snaps]);

  const isDragging = !!drag;
  const indentX = level ? mLpx + inToPx(level.indent || 0) : null;
  const hangX = level ? mLpx + inToPx((level.indent || 0) + (level.hangingIndent || 0)) : null;

  // ─── The ruler's ZERO is the LEFT MARGIN (Josh, 2026-08-13) ───
  // It used to number from the PAGE EDGE while the indent markers were placed
  // at `margin + indent`. So a level at indent 0 sat over the "1" on an
  // inch-margin page and nothing on the ruler agreed with the Indent box or
  // the paper — the opposite of WYSIWYG. Word zeroes its ruler at the margin;
  // so does this. Ticks run negative through the margin, which is shaded.
  const mRpx = inToPx(geo.marginRightIn ?? 0);
  const firstInch = -Math.ceil(geo.marginLeftIn);
  const lastInch = Math.ceil(geo.pageWidthIn - geo.marginLeftIn);
  const inchX = (inch: number) => mLpx + inch * PX_PER_IN;

  return (
    // The ruler used to be aria-hidden wholesale, which silently hid two
    // focusable sliders from assistive tech (§7). The ticks are decorative;
    // the handles are not.
    <div
      className="sfs-ruler"
      ref={rootRef}
      style={{ width: pW, height: RULER_H }}
      role="group"
      aria-label="Page ruler — level indents"
    >
      {/* The margins, shaded — so "0" is visibly where the text starts. */}
      <div
        className="sfs-ruler-margin"
        aria-hidden="true"
        style={{ left: 0, width: Math.max(0, mLpx) }}
      />
      <div
        className="sfs-ruler-margin"
        aria-hidden="true"
        style={{ left: pW - mRpx, width: Math.max(0, mRpx) }}
      />

      {Array.from({ length: lastInch - firstInch + 1 }, (_, k) => {
        const inch = firstInch + k;
        const x = inchX(inch);
        if (x < -1 || x > pW + 1) return null;
        return (
          <React.Fragment key={inch}>
            <span className="sfs-ruler-num" aria-hidden="true" style={{ left: x + 3 }}>
              {Math.abs(inch)}
            </span>
            <div
              className="sfs-ruler-tick sfs-ruler-tick-major"
              aria-hidden="true"
              style={{ left: x }}
            />
            {inch < lastInch &&
              [1, 2, 3].map((q) => {
                const qx = x + q * (PX_PER_IN / 4);
                if (qx < 0 || qx > pW) return null;
                return (
                  <div
                    key={q}
                    className={`sfs-ruler-tick${q === 2 ? ' sfs-ruler-tick-mid' : ''}`}
                    aria-hidden="true"
                    style={{ left: qx }}
                  />
                );
              })}
          </React.Fragment>
        );
      })}

      {/* Discoverability (Josh, 2026-08-22: "those handles don't exist"). The
          markers only ever appear for the SELECTED level, so with nothing
          selected the ruler looked inert. It now says what to do. */}
      {!level && <span className="sfs-ruler-hint">Click a paragraph to drag its indents</span>}

      {level && indentX != null && hangX != null && (
        <>
          <div
            className="sfs-ruler-span"
            aria-hidden="true"
            style={{
              left: Math.min(indentX, hangX),
              width: Math.max(1, Math.abs(hangX - indentX)),
            }}
          />
          <div
            role="slider"
            tabIndex={0}
            aria-label={`Level ${levelFocus} indent — ${(level.indent || 0).toFixed(2)} inches`}
            aria-valuenow={level.indent || 0}
            title={`Indent · ${(level.indent || 0).toFixed(2)}″ — drag to change`}
            className={`sfs-ruler-handle sfs-ruler-handle-indent${drag?.type === 'indent' ? ' is-active' : ''}`}
            style={{ left: indentX }}
            onMouseDown={beginDrag('indent')}
            onKeyDown={(e) => {
              if (idx == null) return;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const dir = e.key === 'ArrowRight' ? 1 : -1;
                nudgeIndent(edit, idx, 'indent', dir * nudgeStep(e.shiftKey));
              }
            }}
          />
          <div
            role="slider"
            tabIndex={0}
            aria-label={`Level ${levelFocus} hanging indent — ${(level.hangingIndent || 0).toFixed(2)} inches`}
            aria-valuenow={level.hangingIndent || 0}
            title={`Hanging · ${((level.indent || 0) + (level.hangingIndent || 0)).toFixed(2)}″ — drag to change`}
            className={`sfs-ruler-handle sfs-ruler-handle-hanging${drag?.type === 'hanging' ? ' is-active' : ''}`}
            style={{ left: hangX }}
            onMouseDown={beginDrag('hanging')}
            onKeyDown={(e) => {
              if (idx == null) return;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const dir = e.key === 'ArrowRight' ? 1 : -1;
                // `nudgeIndent` carries the same Order-3 clamp the drag has:
                // hanging never goes negative, so keyboard nudges can't
                // reintroduce the crossing bug either.
                nudgeIndent(edit, idx, 'hanging', dir * nudgeStep(e.shiftKey));
              }
            }}
          />
        </>
      )}

      {isDragging && liveVal != null && (
        <div
          className="sfs-ruler-readout"
          aria-hidden="true"
          style={{ left: (drag!.type === 'indent' ? indentX : hangX) ?? 0 }}
        >
          {liveVal.toFixed(2)}″ {drag!.type === 'indent' ? 'indent' : 'hanging'}
        </div>
      )}
    </div>
  );
}
